import { useEffect } from "react";
import useQueueStore from "../../stores/queue-store";
import { Modal } from "antd";
import { ModalHeader } from "ui";
import { SlotSelectionContent } from "../../molecules/cards/queue/select-slot";
import { EKA_EVENT } from "../../utils/bridges";
import { Appointment } from "@elixir/types";
import dayjs from "dayjs";
import {
  getCalendarConfigs,
  getEvitalsConfig,
} from "../../utils/bridges/methods";

const ExposableSelectSlotModal = ({
  attributes,
  elementRef,
}: {
  attributes: Record<string, string>;
  elementRef: HTMLElement;
}) => {
  const {
    docid: docId,
    ptid: ptId,
    clinicid: clinicId,
    show,
    returnfullapt,
  } = attributes;

  const setSelectSlotInfo = useQueueStore((state) => state.setSelectSlotInfo);
  const setClinicId = useQueueStore((state) => state.setClinicId);
  const setSelectedAppointment = useQueueStore(
    (state) => state.setSelectedAppointment
  );
  const setSelectedDate = useQueueStore((state) => state.setSelectedDate);

  const setCalendarConfigs = useQueueStore((state) => state.setCalendarConfigs);

  useEffect(() => {
    async function init() {
      const calendarConfigs = await getCalendarConfigs();

      setCalendarConfigs(calendarConfigs);
    }

    init();
  }, []);

  useEffect(() => {
    setSelectSlotInfo({
      bookType: "SCHEDULE",
      aptChannel: "BOOKED",
      showModal: true,
    });

    setClinicId(clinicId);
  }, [docId]);

  const selectSlotAptChannel = useQueueStore(
    (state) => state.selectSlotAptChannel
  );
  const selectSlotBookType = useQueueStore((state) => state.selectSlotBookType);

  const handleClose = () => {
    setSelectedAppointment(undefined);
    setSelectSlotInfo({
      aptChannel: undefined,
      bookType: undefined,
      showModal: false,
    });

    elementRef.dispatchEvent(
      new CustomEvent(EKA_EVENT, { detail: { type: "CLOSE_SLOT_MODAL" } })
    );
  };

  const handleSubmit = (apt?: Pick<Appointment, "full_date"> | Appointment) => {
    setSelectedAppointment(undefined);
    setSelectSlotInfo({
      aptChannel: undefined,
      bookType: undefined,
      showModal: false,
    });

    elementRef.dispatchEvent(
      new CustomEvent(EKA_EVENT, { detail: { type: "CLOSE_SLOT_MODAL" } })
    );

    if (apt) {
      const shouldReturnFullApt = returnfullapt === "true";
      const isFullAppointment = "aid" in apt || "doctor_oid" in apt;

      let fullDate: string | Date;
      if (isFullAppointment) {
        const aptFullDate = (apt as any).full_date;
        fullDate = aptFullDate?.toDate
          ? aptFullDate.toDate().toISOString()
          : aptFullDate;
      } else {
        fullDate = (apt as Pick<Appointment, "full_date">).full_date;
      }

      const fullDateForStore =
        typeof fullDate === "string" ? new Date(fullDate) : fullDate;
      setSelectedDate(dayjs(fullDateForStore).startOf("day").toDate());

      // Send full appointment if requested and available, otherwise send what we have
      const aptToSend =
        shouldReturnFullApt && isFullAppointment
          ? (apt as Appointment)
          : {
              full_date:
                typeof fullDate === "string"
                  ? fullDate
                  : fullDate.toISOString(),
            };

      elementRef.dispatchEvent(
        new CustomEvent(EKA_EVENT, {
          detail: { type: "SLOT_MODAL_APT_BKD", apt: aptToSend },
        })
      );
    } else {
      elementRef.dispatchEvent(
        new CustomEvent(EKA_EVENT, { detail: { type: "CLOSE_SLOT_MODAL" } })
      );
    }
  };

  return (
    <Modal
      style={{
        padding: 0,
        borderRadius: "0.5rem",
      }}
      bodyStyle={{ padding: 0 }}
      footer={null}
      closable={false}
      open={show === "true"}
      className="dr-min-h-520 overflow-y-auto dr-h-520 dr-max-w-408 dr-w-408 dr-bg-darwin-touch-neutral-0 BodyBody2Bold dr-rounded-16  flex dr-flex-col dr-justify-between dr-relative"
      onCancel={handleClose}
      destroyOnClose={true}
    >
      <ModalHeader
        onClose={handleClose}
        title={
          selectSlotBookType === "RESCHEDULE"
            ? "Reschedule Appointment"
            : "Schedule Appointment"
        }
      />
      <SlotSelectionContent
        aptData={
          {
            doctor_oid: docId,
            patient_oid: ptId,
            clinic_id: clinicId,
          } as Appointment
        }
        aptChannel={selectSlotAptChannel}
        bookType={selectSlotBookType}
        onSubmitClick={handleSubmit}
      />
    </Modal>
  );
};

export default ExposableSelectSlotModal;
